import React from 'react'
import { createPortal } from 'react-dom'
import {
  CartesianGrid,
  ComposedChart,
  ReferenceArea,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts'
import styled from 'styled-components'

import { ChartDatapointHoverAnnotation } from 'features/charts-v2/components/ChartDatapointHoverAnnotation'
import type { ChartHoverAnnotationRow } from 'features/charts-v2/components/ChartDatapointHoverAnnotation'
import {
  getRatingBarColor,
  getSentimentBarColor,
  RATING_THRESHOLDS,
  RATING_ZONE_COLORS,
  SENTIMENT_THRESHOLDS,
  SENTIMENT_ZONE_COLORS,
  SentimentColorGroup,
} from 'features/charts-v2/constants'
import { useYAxisLabelStep } from 'features/charts-v2/hooks/use-y-axis-label-step'
import type { RidgelineRow } from 'features/charts-v2/services/data-transformation-service'
import { BaseChartStrategy } from 'features/charts-v2/strategies/Base'
import {
  RidgelineLayer,
  type RidgelineHandlers,
  type RidgelineLabelLayout,
  type RidgelineLayerRow,
} from 'features/charts-v2/strategies/RidgelineLayer'
import type { ChartProps } from 'features/charts-v2/types/chart-strategy'
import {
  calculateHorizontalBarYAxisWidth,
  calculateMaxTextWidth,
  DEFAULT_X_AXIS_HEIGHT_PX,
  formatMetricAxisNumber,
  getMetricAxisDomain,
  HORIZONTAL_BAR_Y_AXIS_TRUNCATE_CHARS,
  MAX_Y_AXIS_LABELS,
} from 'features/charts-v2/utils/axis-tick-utils'
import {
  formatMetricValue,
  VOLUME_LABEL_FOR,
} from 'features/charts-v2/utils/metric-format-utils'
import {
  getRidgelineBins,
  RIDGELINE_AXIS_TICKS,
  type RidgelineMetric,
} from 'features/charts-v2/utils/ridgeline-bins'
import { placeTooltip } from 'features/charts-v2/utils/tooltip-placement'

const NEUTRAL_RIDGE_COLOR = '#bfbfbf'
const MEAN_LABEL_GAP_PX = 6
const MEAN_LABEL_BUFFER_PX = 8
const TOOLTIP_OFFSET_PX = 14
const TOOLTIP_VIEWPORT_MARGIN_PX = 8
// Recharts needs a non-empty dataset to lay out its axes; the ridges are
// drawn from `rows`, not from this.
const AXIS_ANCHOR_ROWS = [{ x: 0 }]

const ChartContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`

// Fixed in the viewport and portalled to the body, so neither the chart pane's
// overflow nor a transformed dashboard-tile ancestor can clip or offset it. The
// real position is written in RidgelineTooltip once its size is known.
const TooltipAnchor = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1000;
  pointer-events: none;
`

interface ZoneSpec {
  x1: number
  x2: number
  fill: string
}

const getZones = (
  metric: RidgelineMetric,
  domain: [number, number],
  showSentimentZones: boolean,
  showRatingZones: boolean,
): ZoneSpec[] => {
  const [min, max] = domain
  if (metric === 'sentiment') {
    if (!showSentimentZones) return []
    return [
      {
        x1: min,
        x2: SENTIMENT_THRESHOLDS.NEGATIVE,
        fill: SENTIMENT_ZONE_COLORS[SentimentColorGroup.NEGATIVE],
      },
      {
        x1: SENTIMENT_THRESHOLDS.POSITIVE,
        x2: max,
        fill: SENTIMENT_ZONE_COLORS[SentimentColorGroup.POSITIVE],
      },
    ]
  }
  if (!showRatingZones) return []
  return [
    {
      x1: min,
      x2: RATING_THRESHOLDS.NEGATIVE,
      fill: RATING_ZONE_COLORS.negative,
    },
    {
      x1: RATING_THRESHOLDS.POSITIVE,
      x2: max,
      fill: RATING_ZONE_COLORS.positive,
    },
  ]
}

const toLayerRow = (
  row: RidgelineRow,
  metric: RidgelineMetric,
): RidgelineLayerRow => {
  if (row.mean == null) {
    return { ...row, color: NEUTRAL_RIDGE_COLOR }
  }
  return {
    ...row,
    color:
      metric === 'sentiment'
        ? getSentimentBarColor(row.mean)
        : getRatingBarColor(row.mean),
  }
}

/** Everything the chart takes from the panel's display settings. */
export interface RidgelineDisplay {
  scaleByVolume: boolean
  fontSizePx: number
  showXAxisLabel: boolean
  showYAxisLabel: boolean
  showDatapointLabels: boolean
  yAxisOverflow: NonNullable<ChartProps['yAxisOverflow']>
  showSentimentZones: boolean
  showRatingZones: boolean
  metricLabel: string
  commonMargin: ReturnType<BaseChartStrategy['getCommonMargin']>
}

interface RidgelineFrameProps {
  rows: RidgelineLayerRow[]
  metric: RidgelineMetric
  display: RidgelineDisplay
  rowNameStep: number
  handlers: RidgelineHandlers
}

// Memoised so a hover (which only moves the tooltip) never re-renders Recharts.
const RidgelineFrame = React.memo<RidgelineFrameProps>(
  ({ rows, metric, display, rowNameStep, handlers }) => {
    const {
      scaleByVolume,
      fontSizePx,
      showXAxisLabel,
      showYAxisLabel,
      showDatapointLabels,
      yAxisOverflow,
      showSentimentZones,
      showRatingZones,
      metricLabel,
      commonMargin,
    } = display
    const xDomain = getMetricAxisDomain(metric)!
    const yAxisWidth = showYAxisLabel
      ? calculateHorizontalBarYAxisWidth(
          rows.map((row) => row.name),
          yAxisOverflow,
          fontSizePx,
        )
      : 10

    // The mean label sits right of the plot, so widen the right margin to fit
    // the widest one (same measuring the bar chart does for its value labels).
    const labelFont = `${fontSizePx}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`
    const meanLabels = showDatapointLabels
      ? rows.flatMap((row) =>
          row.mean == null ? [] : [formatMetricValue(row.mean, metric)],
        )
      : []
    const rightMargin =
      meanLabels.length > 0
        ? Math.max(
            commonMargin.right,
            MEAN_LABEL_GAP_PX +
              calculateMaxTextWidth(meanLabels, labelFont) +
              MEAN_LABEL_BUFFER_PX,
          )
        : commonMargin.right

    const labels: RidgelineLabelLayout = {
      showRowNames: showYAxisLabel,
      showMeans: showDatapointLabels,
      fontSizePx,
      overflow: yAxisOverflow,
      truncateChars: HORIZONTAL_BAR_Y_AXIS_TRUNCATE_CHARS,
      wrapWidthPx: yAxisWidth - 16,
      rowNameStep,
    }

    return (
      <ResponsiveContainer width="100%" height="100%" debounce={100}>
        <ComposedChart
          data={AXIS_ANCHOR_ROWS}
          margin={{ ...commonMargin, right: rightMargin }}
        >
          <CartesianGrid strokeDasharray="3 3" horizontal={false} />
          {getZones(metric, xDomain, showSentimentZones, showRatingZones).map(
            (zone, index) => (
              <ReferenceArea
                key={`zone-${index}`}
                x1={zone.x1}
                x2={zone.x2}
                fill={zone.fill}
                fillOpacity={1}
              />
            ),
          )}
          <YAxis
            type="number"
            domain={[0, 1]}
            width={yAxisWidth}
            tick={false}
          />
          <XAxis
            type="number"
            dataKey="x"
            orientation="top"
            domain={xDomain}
            ticks={RIDGELINE_AXIS_TICKS[metric]}
            allowDataOverflow
            tick={{ fontSize: fontSizePx }}
            tickFormatter={(value: number) =>
              formatMetricAxisNumber(value, metric)
            }
            label={
              showXAxisLabel
                ? {
                    value: metricLabel,
                    position: 'insideTop',
                    offset: -5,
                    style: {
                      textAnchor: 'middle',
                      fill: '#666',
                      fontSize: fontSizePx,
                    },
                  }
                : undefined
            }
          />
          <RidgelineLayer
            rows={rows}
            metric={metric}
            xDomain={xDomain}
            scaleByVolume={scaleByVolume}
            labels={labels}
            handlers={handlers}
          />
        </ComposedChart>
      </ResponsiveContainer>
    )
  },
)

RidgelineFrame.displayName = 'RidgelineFrame'

interface RidgelineHover {
  rowIndex: number
  binIndex: number
  /** Pointer position in viewport coordinates. */
  clientX: number
  clientY: number
}

interface RidgelineChartViewProps {
  rows: RidgelineRow[]
  metric: RidgelineMetric
  display: RidgelineDisplay
  onDataPointHover?: ChartProps['onDataPointHover']
  onSegmentClick?: ChartProps['onSegmentClick']
}

const buildTooltipRows = (
  row: RidgelineLayerRow,
  binIndex: number,
  metric: RidgelineMetric,
): ChartHoverAnnotationRow[] => {
  const bin = row.bins[binIndex]!
  const tooltipRows: ChartHoverAnnotationRow[] = [
    { label: 'Bucket', value: bin.label, percentage: null, color: null },
    {
      label: VOLUME_LABEL_FOR[metric] ?? 'Count',
      value: bin.count.toLocaleString(),
      percentage: `${(bin.pct * 100).toFixed(1)}%`,
      color: null,
    },
  ]
  if (row.mean != null) {
    tooltipRows.push({
      label: `Average ${metric}`,
      value: formatMetricValue(row.mean, metric),
      percentage: null,
      color: row.color,
    })
  }
  tooltipRows.push({
    label: `Total ${(VOLUME_LABEL_FOR[metric] ?? 'count').toLowerCase()}`,
    value: row.n.toLocaleString(),
    percentage: null,
    color: null,
  })
  return tooltipRows
}

interface RidgelineTooltipProps {
  row: RidgelineLayerRow
  hover: RidgelineHover
  metric: RidgelineMetric
}

const RidgelineTooltip: React.FC<RidgelineTooltipProps> = ({
  row,
  hover,
  metric,
}) => {
  const anchorRef = React.useRef<HTMLDivElement>(null)

  // Place from the tooltip's real size. A layout effect runs before paint, so
  // the tooltip never shows at a stale position, and writing the style directly
  // avoids a second render per pointer move.
  React.useLayoutEffect(() => {
    const anchor = anchorRef.current
    if (!anchor) return
    const { left, top } = placeTooltip({
      cursor: { x: hover.clientX, y: hover.clientY },
      size: { width: anchor.offsetWidth, height: anchor.offsetHeight },
      viewport: { width: window.innerWidth, height: window.innerHeight },
      offset: TOOLTIP_OFFSET_PX,
      margin: TOOLTIP_VIEWPORT_MARGIN_PX,
    })
    anchor.style.left = `${left}px`
    anchor.style.top = `${top}px`
  }, [hover])

  return createPortal(
    <TooltipAnchor ref={anchorRef}>
      <ChartDatapointHoverAnnotation
        title={row.name}
        rows={buildTooltipRows(row, hover.binIndex, metric)}
        footer={null}
        focusedField={null}
      />
    </TooltipAnchor>,
    document.body,
  )
}

const RidgelineChartViewInner: React.FC<RidgelineChartViewProps> = ({
  rows,
  metric,
  display,
  onDataPointHover,
  onSegmentClick,
}) => {
  const { fontSizePx, commonMargin } = display
  const layerRows = React.useMemo(
    () => rows.map((row) => toLayerRow(row, metric)),
    [rows, metric],
  )

  const { containerRef, step } = useYAxisLabelStep({
    itemCount: rows.length,
    fontSizePx,
    maxLabels: MAX_Y_AXIS_LABELS,
    chromeHeightPx:
      commonMargin.top + commonMargin.bottom + DEFAULT_X_AXIS_HEIGHT_PX,
  })

  const [hover, setHover] = React.useState<RidgelineHover | null>(null)
  const hoveredRowNameRef = React.useRef<string | null>(null)

  const handleRidgeHover = React.useCallback(
    (
      rowIndex: number,
      binIndex: number,
      clientX: number,
      clientY: number,
    ): void => {
      setHover({ rowIndex, binIndex, clientX, clientY })
      const name = layerRows[rowIndex]?.name ?? null
      if (name !== hoveredRowNameRef.current) {
        hoveredRowNameRef.current = name
        onDataPointHover?.(name)
      }
    },
    [layerRows, onDataPointHover],
  )

  const handleRidgeLeave = React.useCallback((): void => {
    setHover(null)
    hoveredRowNameRef.current = null
    onDataPointHover?.(null)
  }, [onDataPointHover])

  const handleRidgeClick = React.useCallback(
    (rowIndex: number, binIndex: number, event: React.MouseEvent): void => {
      const row = layerRows[rowIndex]
      const bin = getRidgelineBins(metric)[binIndex]
      if (!onSegmentClick || !row || !bin) return
      onSegmentClick(
        {
          primaryBreakdown: row.name,
          value: row.mean ?? 0,
          payload: {
            name: row.name,
            category: row.category,
            count: row.mean,
            volumeCount: row.n,
            ridgelineMetric: metric,
            ridgelineBin: bin.key,
          },
        },
        event,
      )
    },
    [layerRows, metric, onSegmentClick],
  )

  const handlers = React.useMemo<RidgelineHandlers>(
    () => ({
      onHover: handleRidgeHover,
      onLeave: handleRidgeLeave,
      onClick: handleRidgeClick,
    }),
    [handleRidgeHover, handleRidgeLeave, handleRidgeClick],
  )

  const hoveredRow = hover ? layerRows[hover.rowIndex] : undefined

  return (
    <ChartContainer ref={containerRef}>
      <RidgelineFrame
        rows={layerRows}
        metric={metric}
        display={display}
        rowNameStep={step}
        handlers={handlers}
      />
      {hover && hoveredRow && (
        <RidgelineTooltip row={hoveredRow} hover={hover} metric={metric} />
      )}
    </ChartContainer>
  )
}

export const RidgelineChartView = React.memo(RidgelineChartViewInner)
